/**
 * B7 · Geo-distance — pure, privacy-first helpers.
 *
 * Clients must never receive another user's raw coordinates. The server-side
 * proximity RPC returns a fuzzed distance; these helpers define the bucketing
 * so display is consistent and exact positions never leak. Haversine is here
 * for any client-side estimate from already-fuzzed inputs (never raw peers).
 */

const EARTH_KM = 6371;
const toRad = (d: number) => (d * Math.PI) / 180;

/** Great-circle distance (km) between two points. Fail-safe: NaN inputs → Infinity. */
export const haversineKm = (
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number,
): number => {
  if ([aLat, aLng, bLat, bLng].some((n) => !Number.isFinite(n))) return Infinity;
  const dLat = toRad(bLat - aLat);
  const dLng = toRad(bLng - aLng);
  const lat1 = toRad(aLat);
  const lat2 = toRad(bLat);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_KM * Math.asin(Math.min(1, Math.sqrt(h)));
};

/**
 * Snap a distance to a privacy bucket so we never reveal a precise radius.
 * <1km → 0.5 steps; <10km → 1km steps; else 5km steps. Negative/NaN → null.
 */
export const fuzzDistanceKm = (km: number | null | undefined): number | null => {
  if (km == null || !Number.isFinite(km) || km < 0) return null;
  if (km < 1) return Math.round(km * 2) / 2;
  if (km < 10) return Math.round(km);
  return Math.round(km / 5) * 5;
};

/** Human label for a fuzzed distance. null → 'nearby' (never "unknown km"). */
export const formatDistance = (km: number | null | undefined): string => {
  const f = fuzzDistanceKm(km ?? null);
  if (f == null) return 'nearby';
  if (f < 1) return '<1 km';
  return `${f} km`;
};
